import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Skeleton,
  Tooltip,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { usePawnMode } from "../../Context/PawnModeContext";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import LoadingScreen from "../LoadingScreen";
import BranchChip from "../BranchChip";
import { useSwitchableBranches } from "../BranchSwitcher";

// ── White + blue theme tokens (mirrors the prototype) ────────────────────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const INK_FAINT = "#94A3B8";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const BG = "#F8FAFC";
const SUCCESS = "#15803D";
const SUCCESS_BG = "#DCFCE7";
const AMBER = "#B45309";
const AMBER_BG = "#FEF3C7";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Compact Indian-currency formatting: crore / lakh / plain.
const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹ ${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹ ${(v / 1e5).toFixed(2)} L`;
  return `₹ ${v.toLocaleString("en-IN")}`;
};

const fmtFull = (n) => `₹ ${(Number(n) || 0).toLocaleString("en-IN")}`;

// How far back "Recent Activity" is considered to reach. Inclusive of today, so
// 7 means today and the six days before it — the window each subsection's
// "View report" link seeds into its report.
const RECENT_WINDOW_DAYS = 7;
// Rows listed per subsection (Recent Loans / Recent Receipts).
const RECENT_ROWS = 5;

// The recent window, in both the shape the reports read from navigation state
// (YYYY-MM-DD) and the shape the "View report" tooltips promise (DD-MMM-YYYY).
// Derived on every render rather than once at module load, so a dashboard left
// open overnight still links to a window that ends today.
const recentWindow = () => {
  const from = dayjs().subtract(RECENT_WINDOW_DAYS - 1, "day");
  const to = dayjs();
  return {
    fromDate: from.format("YYYY-MM-DD"),
    toDate: to.format("YYYY-MM-DD"),
    label: `${from.format("DD-MMM-YYYY")} to ${to.format("DD-MMM-YYYY")}`,
  };
};

const relativeDate = (d) => {
  if (!d) return "";
  const day = dayjs(d);
  const now = dayjs();
  const diff = now.diff(day, "day");
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff} days ago`;
  return day.format("DD MMM YYYY");
};

// A single grey bar standing in for a figure that is still being fetched. Every
// refresh keeps the dashboard's chrome — cards, headings, icons, labels — on
// screen and swaps only the computed numbers for these, so a reload reads as
// "the values are recalculating", never as the page disappearing and coming
// back. Sized in the caller so each placeholder occupies the same box the real
// value will, and nothing shifts when it lands.
const Shimmer = ({ w, h = 12, sx }) => (
  <Skeleton
    variant="rounded"
    animation="wave"
    sx={{ width: w, height: h, borderRadius: "6px", bgcolor: "#EEF2F7", ...sx }}
  />
);

// Reusable KPI card matching the prototype's stat tiles.
const StatCard = ({
  icon,
  iconBg,
  iconColor,
  value,
  label,
  badge,
  badgeColor,
  badgeBg,
  onClick,
  loading,
}) => (
  <Box
    onClick={onClick}
    sx={{
      background: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: "14px",
      p: { xs: 1.5, md: 2 },
      cursor: onClick ? "pointer" : "default",
      height: "100%",
      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
      "&:hover": onClick
        ? {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 25px -5px rgba(15,23,42,0.08)",
            borderColor: "#CBD5E1",
          }
        : {},
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 20, color: iconColor } })}
      </Box>
      {badge != null &&
        (loading ? (
          <Shimmer w={62} h={18} />
        ) : (
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 600,
              color: badgeColor,
              bgcolor: badgeBg,
              px: 0.75,
              py: 0.25,
              borderRadius: "6px",
              whiteSpace: "nowrap",
            }}
          >
            {badge}
          </Typography>
        ))}
    </Box>
    {loading ? (
      // Boxed to the exact line-box the figure would fill, so the label below
      // does not jump when the number arrives.
      <Box
        sx={{
          height: { xs: "1.43rem", sm: "1.76rem" },
          display: "flex",
          alignItems: "center",
        }}
      >
        <Shimmer w={{ xs: 84, sm: 104 }} h={20} />
      </Box>
    ) : (
      <Typography
        sx={{
          fontSize: { xs: "1.3rem", sm: "1.6rem" },
          fontWeight: 700,
          color: INK,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
    )}
    <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED, mt: 0.25 }}>
      {label}
    </Typography>
  </Box>
);

// One row of a Recent Activity subsection. Loans and receipts are normalised to
// the same shape upstream (see recentLoans / recentReceipts), so this renders
// both: an initials disc, code · customer with a grey sub-line, and the amount
// over a coloured status word. `highlight` picks the green accent (a settled
// loan / a settlement receipt) over the default blue.
//
// The sub-line also carries the branch the row happened at. This list is
// store-wide — a loan raised at one branch shows on every branch's dashboard,
// which is exactly why the reader needs telling WHERE it happened. It stays a
// label: nothing here filters by branch. On a single-branch store the chip
// renders nothing at all (see BranchChip), so the line is unchanged for the
// stores that have only one place to be.
const ActivityRow = ({ row, first, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1.25,
      borderTop: first ? "none" : `1px solid ${BORDER}`,
      cursor: "pointer",
      "&:hover": { bgcolor: BG },
      borderRadius: "8px",
      px: 1,
      mx: -1,
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        bgcolor: row.highlight ? SUCCESS_BG : BLUE_PALE,
        color: row.highlight ? SUCCESS : BLUE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.72rem",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {(row.customer || "?").slice(0, 2).toUpperCase()}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.82rem", color: INK_SOFT }}>
        <b style={{ fontFamily: "monospace", color: INK }}>{row.code}</b>
        {" · "}
        {row.customer}
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
      >
        <Typography noWrap sx={{ fontSize: "0.7rem", color: INK_FAINT }}>
          {row.secondary}
        </Typography>
        <BranchChip
          branchName={row.branchName}
          branchCode={row.branchCode}
          storeName={row.storeName}
          branchCount={row.branchCount}
          dense
        />
      </Box>
    </Box>
    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
      <Typography
        sx={{
          fontSize: "0.82rem",
          fontWeight: 600,
          color: INK,
          fontFamily: "monospace",
        }}
      >
        {fmtFull(row.amount)}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          color: row.highlight ? SUCCESS : BLUE,
        }}
      >
        {row.tag}
      </Typography>
    </Box>
  </Box>
);

// The placeholder twin of ActivityRow. Every box matches its real counterpart's
// geometry — the 34px disc, both left lines, both right lines — so a list of
// these is exactly as tall as the list that replaces it.
const ActivityRowSkeleton = ({ first }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1.25,
      borderTop: first ? "none" : `1px solid ${BORDER}`,
      px: 1,
      mx: -1,
    }}
  >
    <Skeleton
      variant="circular"
      animation="wave"
      sx={{ width: 34, height: 34, flexShrink: 0, bgcolor: "#EEF2F7" }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Shimmer w="58%" h={11} />
      <Shimmer w="32%" h={9} sx={{ mt: 0.6 }} />
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        flexShrink: 0,
      }}
    >
      <Shimmer w={68} h={11} />
      <Shimmer w={42} h={9} sx={{ mt: 0.6 }} />
    </Box>
  </Box>
);

// A titled subsection of the Recent Activity panel: sub-heading, its own
// "View report" link (tooltipped with the exact window that report will open
// on), and the rows — or a single line when there are none.
const ActivitySection = ({
  title,
  tooltip,
  rows,
  emptyText,
  onView,
  onRowClick,
  loading,
}) => (
  <Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 0.5,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 700,
          color: INK_MUTED,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {title}
      </Typography>
      <Tooltip title={tooltip} placement="top">
        <Button
          onClick={onView}
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontSize: "0.78rem",
            fontWeight: 600,
            color: BLUE,
            minWidth: 0,
            "&:hover": { bgcolor: BLUE_PALE },
          }}
        >
          View report
        </Button>
      </Tooltip>
    </Box>

    {loading ? (
      // As many placeholders as the section is currently showing, so a refresh
      // neither grows nor shrinks the panel. Only a first-ever empty list falls
      // back to a nominal five.
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {Array.from({ length: rows.length || 5 }).map((_, i) => (
          <ActivityRowSkeleton key={i} first={i === 0} />
        ))}
      </Box>
    ) : rows.length === 0 ? (
      <Typography
        sx={{
          color: INK_FAINT,
          fontSize: "0.85rem",
          py: 2.5,
          textAlign: "center",
        }}
      >
        {emptyText}
      </Typography>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {rows.map((r, i) => (
          <ActivityRow
            key={r.id ?? i}
            row={r}
            first={i === 0}
            onClick={() => onRowClick(r)}
          />
        ))}
      </Box>
    )}
  </Box>
);

function PawnShopDashboard() {
  const navigate = useNavigate();
  // Pawn mode: 1 = official, 2 = unofficial. The dashboard only ever considers
  // records belonging to the currently-active mode.
  const { mode: pawnMode } = usePawnMode() || {};

  const { currentStoreInfo: contextStoreInfo } =
    useContext(AutoTaskAuthContext) || {};

  const loggedUser =
    JSON.parse(localStorage.getItem("CapacitorStorage.user")) || {};
  const storedStoreInfo =
    JSON.parse(localStorage.getItem("CapacitorStorage.current_store_info")) ||
    {};

  // Context wins when hydrated so the header follows a switch immediately.
  const currentStoreInfo = contextStoreInfo || storedStoreInfo;

  const user_id = loggedUser.id;
  const user_name = loggedUser.name;
  const user_mobile = loggedUser.mobile;
  const store_id = currentStoreInfo.id;
  const store_name = currentStoreInfo.name;

  const userDataObject = useMemo(
    () => ({ user_id, user_mobile, store_id }),
    [user_id, user_mobile, store_id],
  );

  // Two loading flags, deliberately. `loading` covers the FIRST paint only —
  // there is no dashboard to keep on screen yet, so that one earns the full-page
  // loader. `refreshing` covers every fetch after it (the refresh button, a mode
  // toggle): the page stays mounted and only the figures it computes turn into
  // placeholders, so nothing ever vanishes and comes back.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const loadedOnce = useRef(false);
  const [error, setError] = useState(null);
  const [loans, setLoans] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [customerMap, setCustomerMap] = useState(new Map());

  // ── Which branch each row happened at ──────────────────────────────────────
  // The activity lists are store-wide (deliberately — see BranchChip), so a
  // payment taken at one branch appears on every branch's dashboard. Naming the
  // branch on the row is what stops that reading as somebody else's money
  // appearing in your shop.
  //
  // The loan and receipt feeds return branch_id, not a name, so the branch list
  // is fetched once and used as the lookup. Reusing the switcher's hook rather
  // than fetching again: it already serves exactly this list, and its
  // `canSwitch` gate is separate from the fetch, so a counter clerk who may not
  // move between branches still gets the names.
  const { branches } = useSwitchableBranches();
  const branchMap = useMemo(() => {
    const map = new Map();
    (branches || []).forEach((b) => map.set(Number(b.id), b));
    return map;
  }, [branches]);
  // Everything a row needs to decide whether its branch is worth printing.
  // branchCount matters: on a single-branch store BranchChip suppresses a
  // branch that just repeats the store's name, which is the majority of stores.
  const branchFieldsFor = useCallback(
    (branchId) => {
      const branch =
        branchId === null || branchId === undefined
          ? null
          : branchMap.get(Number(branchId));
      return {
        branchName: branch?.name || null,
        branchCode: branch?.code || null,
        storeName: store_name,
        branchCount: branchMap.size,
      };
    },
    [branchMap, store_name],
  );

  // ── Store switcher ─────────────────────────────────────────────────────────
  // The picker itself lives in the pawn sidebar (see PawnLayout / StoreSwitcher)
  // so one control serves every pawn screen. All that is left here is the tail of
  // a switch that landed on this dashboard.
  //
  // Set just before switchStore leaves the page, so the overlay can carry across
  // the reload and stay up until this store's data has actually landed.
  const [postSwitchLabel, setPostSwitchLabel] = useState(() =>
    sessionStorage.getItem("pawnStoreSwitching"),
  );

  useEffect(() => {
    if (!loading && postSwitchLabel) {
      sessionStorage.removeItem("pawnStoreSwitching");
      setPostSwitchLabel(null);
    }
  }, [loading, postSwitchLabel]);

  const loadData = useCallback(async () => {
    if (store_id == null) {
      setLoading(false);
      setRefreshing(false);
      setError("No store selected.");
      return;
    }
    if (loadedOnce.current) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [payRes, recRes, custRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/pawn/payments/store/${store_id}`, {
          // The active mode MUST be sent: the endpoint is official-by-default
          // (fail safe), so without it unofficial mode would be served the
          // official series and then filtered to nothing client-side.
          params: {
            limit: 5000,
            page: 1,
            mode: Number(pawnMode) === 2 ? 2 : 1,
          },
        }),
        // The receipts register. This endpoint windows on receipt_date and has
        // no limit, so omitting the dates pulls the whole history — the same
        // query the Receipt Report's "Show all" makes. That is what "the newest
        // receipts, however old they are" needs, matching how the loans list
        // above takes the newest rows out of the full payments feed. `mode` is
        // mandatory here for the same fail-safe reason.
        axios.get(`${API_BASE_URL}/api/pawn/receipts/store/${store_id}`, {
          params: { mode: Number(pawnMode) === 2 ? 2 : 1 },
        }),
        axios.get(`${API_BASE_URL}/api/pawn/customers/store/${store_id}`, {
          params: { limit: 5000, sort: "ASC" },
        }),
      ]);
      setLoans(payRes.data?.data || []);
      setReceipts(recRes.data?.data || []);
      const map = new Map();
      // Customer records key on customer_id (loans reference it via cust_id).
      (custRes.data?.data || []).forEach((c) => map.set(c.customer_id, c));
      setCustomerMap(map);
    } catch (err) {
      setError("Failed to load dashboard data.");
    } finally {
      // Flipped before the flags so a failed first load still hands the page
      // over to the in-place treatment on the retry rather than blanking again.
      loadedOnce.current = true;
      setLoading(false);
      setRefreshing(false);
    }
    // Refetches when the mode is toggled — the server-side series changes with it.
  }, [store_id, pawnMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Loan records for the active pawn mode. The store feed contains both loans
  // (type "P") and receipts (type "R"); receipts reuse the parent loan's
  // voucher_no and loan_amount, so only "P" rows represent actual loans.
  // Status 3 (official-clean) is shared — the two books agree on it — so it
  // shows in BOTH modes: official mode (1) sees status 1 (altered) + 3, and
  // unofficial mode (2) sees status 2 + 3. Only status 1 is official-exclusive.
  const visibleLoans = useMemo(() => {
    const wantUnofficial = Number(pawnMode) === 2;
    return loans.filter((p) => {
      if (p.type !== "P") return false;
      const st = Number(p.status);
      return wantUnofficial ? st === 2 || st === 3 : st !== 2;
    });
  }, [loans, pawnMode]);
  // Receipts for the active mode. The register endpoint already applies this
  // rule server-side via `mode`; re-applying it here is the same belt-and-braces
  // the loans list uses. Note there is NO type check: unlike the payments feed,
  // every row from this endpoint IS a receipt (a "P" filter would empty it).
  const visibleReceipts = useMemo(() => {
    const wantUnofficial = Number(pawnMode) === 2;
    return receipts.filter((r) => {
      const st = Number(r.status);
      return wantUnofficial ? st === 2 || st === 3 : st !== 2;
    });
  }, [receipts, pawnMode]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  // Loans only. Receipts are deliberately NOT folded in: a receipt is a
  // repayment against a loan already counted below, so including them would
  // double-count the loan book and the ticket totals.
  const stats = useMemo(() => {
    const now = dayjs();
    const today = now.format("YYYY-MM-DD");
    let loanBook = 0;
    let activeCount = 0;
    let settledCount = 0;
    let todayCount = 0;
    let monthDisbursed = 0;
    let monthCount = 0;

    visibleLoans.forEach((p) => {
      const amt = parseFloat(p.loan_amount) || 0;
      // Settlement is driven solely by is_settled ("Y" = settled, else active).
      const settled = String(p.is_settled).toUpperCase() === "Y";
      const txDate = p.transaction_date
        ? dayjs(p.transaction_date).format("YYYY-MM-DD")
        : "";
      if (settled) {
        settledCount += 1;
      } else {
        activeCount += 1;
        loanBook += amt;
      }
      if (txDate === today) todayCount += 1;
      if (
        p.transaction_date &&
        dayjs(p.transaction_date).isSame(now, "month")
      ) {
        monthDisbursed += amt;
        monthCount += 1;
      }
    });

    const avgLoan = activeCount ? loanBook / activeCount : 0;

    return {
      loanBook,
      activeCount,
      settledCount,
      todayCount,
      monthDisbursed,
      monthCount,
      avgLoan,
      customerCount: customerMap.size,
    };
  }, [visibleLoans, customerMap]);

  // ── Recent activity rows ───────────────────────────────────────────────────
  // Both subsections render through the same row component, so each memo
  // normalises its records to one display shape:
  //   code       the bold monospace identifier
  //   secondary  the small grey line under it
  //   tag        the coloured word under the amount
  //   highlight  true = the green (closed / settled) accent, false = blue
  // Receipt rows also carry `settlement`, which decides WHICH screen the row
  // opens — kept separate from `highlight`, since that is only a colour and a
  // settled loan shares it without being a settlement.
  // Newest first by id — creation order, the same order the Payment and Receipt
  // screens' chevrons walk — not by date, so same-day rows keep their sequence.
  const recentLoans = useMemo(() => {
    return [...visibleLoans]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, RECENT_ROWS)
      .map((p) => {
        const cust = customerMap.get(p.cust_id);
        const settled = String(p.is_settled).toUpperCase() === "Y";
        return {
          id: p.id,
          ticket: p.voucher_no,
          code: p.voucher_no,
          customer: cust?.name || "—",
          amount: parseFloat(p.loan_amount) || 0,
          secondary: relativeDate(p.transaction_date),
          tag: settled ? "Settled" : "Active",
          highlight: settled,
          // Where the loan was RAISED.
          ...branchFieldsFor(p.branch_id),
        };
      });
  }, [visibleLoans, customerMap, branchFieldsFor]);

  // Receipts come off the register endpoint with their parent loan's customer
  // already joined in, so there is no customerMap lookup here. The bold code is
  // the RECEIPT number (voucher_ref — "REC…" for a repayment, "H…" for a
  // settlement); the loan it was paid against goes on the second line, since
  // that is the ticket the row opens.
  const recentReceipts = useMemo(() => {
    return [...visibleReceipts]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .slice(0, RECENT_ROWS)
      .map((r) => ({
        id: r.id,
        ticket: r.voucher_no,
        code: r.voucher_ref || r.voucher_no,
        customer: r.customer_name || "—",
        amount: parseFloat(r.amount) || 0,
        secondary: `${relativeDate(r.receipt_date)} · #${r.voucher_no}`,
        tag: r.is_settlement ? "Settlement" : "Receipt",
        highlight: !!r.is_settlement,
        settlement: !!r.is_settlement,
        // Where the money was TAKEN, which is allowed to differ from the
        // branch the loan was raised at — a customer can pay at whichever
        // branch is nearest them, and that is the whole point of showing it.
        ...branchFieldsFor(r.branch_id),
      }));
  }, [visibleReceipts, branchFieldsFor]);

  // The window both "View report" links open on — and the exact dates their
  // tooltips name. One value for both, so the promise and the report agree.
  const recentRange = recentWindow();

  const kpis = [
    {
      icon: <AccountBalanceRoundedIcon />,
      iconBg: BLUE_PALE,
      iconColor: BLUE,
      value: fmtMoney(stats.loanBook),
      label: "Active Loan Book",
      badge: `${stats.activeCount} tickets`,
      badgeColor: BLUE_DARK,
      badgeBg: BLUE_PALE,
      // The cash book, not the payment entry screen: from the dashboard this
      // tile is a "what moved on the book" question, not a data-entry one.
      // (It used to open the Day Book chip on Reports; that register now lives
      // on Ledger Reports as the Cash Book.)
      onClick: () => navigate("/pawn/accounts-ledger?type=cashbook"),
    },
    {
      icon: <AutoAwesomeRoundedIcon />,
      iconBg: SUCCESS_BG,
      iconColor: SUCCESS,
      value: stats.todayCount,
      label: "Loans Issued Today",
      badge: "Today",
      badgeColor: SUCCESS,
      badgeBg: SUCCESS_BG,
      // The payments register for today — the loans behind this count.
      onClick: () =>
        navigate("/pawn/reports?type=payment", {
          state: {
            ...userDataObject,
            dateMode: "single",
            singleDate: dayjs().format("YYYY-MM-DD"),
          },
        }),
    },
    {
      icon: <TrendingUpRoundedIcon />,
      iconBg: "#F5F3FF",
      iconColor: "#7C3AED",
      value: fmtMoney(stats.monthDisbursed),
      label: "Disbursed This Month",
      badge: `${stats.monthCount} loans`,
      badgeColor: "#6D28D9",
      badgeBg: "#F5F3FF",
      // Open the report already scoped to the current calendar month (range
      // mode) rather than its default single-day-today view.
      onClick: () =>
        navigate("/pawn/reports?type=payment", {
          state: {
            ...userDataObject,
            dateMode: "range",
            fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
            toDate: dayjs().endOf("month").format("YYYY-MM-DD"),
          },
        }),
    },
    {
      icon: <TaskAltRoundedIcon />,
      iconBg: AMBER_BG,
      iconColor: AMBER,
      value: stats.settledCount,
      label: "Settled / Closed",
      badge: "All time",
      badgeColor: AMBER,
      badgeBg: AMBER_BG,
      // The settlement register, opened year-to-date (1 Jan → today) rather than
      // the receipt entry screen, so the closed loans are listed straight away.
      onClick: () =>
        navigate("/pawn/reports?type=settlement", {
          state: {
            ...userDataObject,
            dateMode: "range",
            fromDate: dayjs().startOf("year").format("YYYY-MM-DD"),
            toDate: dayjs().format("YYYY-MM-DD"),
          },
        }),
    },
    {
      icon: <PeopleAltRoundedIcon />,
      iconBg: "#ECFEFF",
      iconColor: "#0891B2",
      value: stats.customerCount,
      label: "Total Customers",
      badge: "Registered",
      badgeColor: "#0E7490",
      badgeBg: "#ECFEFF",
      onClick: () =>
        navigate("/pawn/customer-master", { state: userDataObject }),
    },
  ];

  const quickActions = [
    {
      label: "Payment Screen",
      desc: "Post a payment or issue a loan",
      icon: <PaymentRoundedIcon />,
      route: "/pawn/payment",
    },
    {
      label: "Receipt Screen",
      desc: "Collect interest & part payments",
      icon: <ReceiptLongRoundedIcon />,
      route: "/pawn/receipt",
    },
    {
      label: "Settlement Screen",
      desc: "Close a loan & release the pledge",
      icon: <TaskAltRoundedIcon />,
      route: "/pawn/settlement",
    },
    {
      label: "Reports",
      desc: "Payment, receipt & settlement registers",
      icon: <AssessmentRoundedIcon />,
      route: "/pawn/reports",
    },
    {
      label: "Ledger",
      desc: "Ledger reports, trial balance & day book",
      icon: <AccountBalanceWalletRoundedIcon />,
      route: "/pawn/accounts-ledger",
    },
    {
      label: "Settings",
      desc: "Rates, prefixes & configuration",
      icon: <SettingsRoundedIcon />,
      route: "/pawn/settings",
    },
  ];

  if (loading) {
    // Landing here straight after a switch: keep the same overlay up so the
    // switch reads as one action rather than flashing a second loader.
    if (postSwitchLabel) {
      return (
        <LoadingScreen loadingMessage={`Switching to ${postSwitchLabel}`} />
      );
    }

    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          background: BG,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 12,
        }}
      >
        <CircularProgress size={32} sx={{ color: BLUE }} />
        <Typography sx={{ fontSize: "0.85rem", color: INK_MUTED }}>
          Loading dashboard…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        minWidth: 0,
        overflowX: "hidden",
        background: BG,
        // 32px gutters on a tablet cost more than they buy; the widest step is
        // reserved for real desktop widths.
        p: { xs: 1.5, sm: 2.5, lg: 4 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1.5, md: 2 },
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: "1.15rem", sm: "1.35rem" },
              fontWeight: 700,
              color: INK,
            }}
          >
            {store_name || "Pawn Dashboard"}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: INK_MUTED, mt: 0.25 }}>
            Welcome back{user_name ? `, ${user_name}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={loadData}
            disabled={refreshing}
            aria-label="Refresh"
            sx={{
              minWidth: 0,
              width: 40,
              height: 40,
              p: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: INK_SOFT,
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: "10px",
              "&:hover": { bgcolor: "#F1F5F9" },
              "&.Mui-disabled": { color: INK_FAINT, bgcolor: SURFACE },
            }}
          >
            {/* Spun while the fetch is out: the one moving part that tells the
                user the placeholders below are waiting on something. */}
            <RefreshRoundedIcon
              sx={{
                fontSize: 20,
                animation: refreshing
                  ? "pawnRefreshSpin 0.9s linear infinite"
                  : "none",
                "@keyframes pawnRefreshSpin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </Button>
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            bgcolor: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#B91C1C",
            borderRadius: "10px",
            px: 2,
            py: 1.25,
            mb: 3,
            fontSize: "0.85rem",
          }}
        >
          {error}
        </Box>
      )}

      {/* KPI cards */}
      <Grid
        container
        spacing={{ xs: 1.25, md: 2 }}
        sx={{ mb: { xs: 2, md: 3 } }}
      >
        {kpis.map((k) => (
          <Grid item xs={6} sm={4} md={4} lg={2.4} key={k.label}>
            <StatCard {...k} loading={refreshing} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ xs: 1.5, md: 2 }}>
        {/* Recent activity */}
        <Grid item xs={12} lg={7}>
          <Box
            sx={{
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              p: { xs: 1.75, md: 2.5 },
              height: "100%",
            }}
          >
            {/* <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: INK,
                mb: 1.5,
              }}
            >
              Recent Activity
            </Typography> */}

            {/* Two registers in one panel. Each keeps its OWN "View report"
                link because they open different reports — Payment vs Receipt —
                even though both are scoped to the same trailing window. */}
            <ActivitySection
              title="Recent Loans"
              tooltip={`Payments from ${recentRange.label}`}
              rows={recentLoans}
              loading={refreshing}
              emptyText="No recent loans found."
              onView={() =>
                navigate("/pawn/reports?type=payment", {
                  state: {
                    ...userDataObject,
                    dateMode: "range",
                    fromDate: recentRange.fromDate,
                    toDate: recentRange.toDate,
                  },
                })
              }
              onRowClick={(r) =>
                navigate("/pawn/payment", {
                  state: { ...userDataObject, voucher_no: r.ticket },
                })
              }
            />

            <ActivitySection
              title="Recent Receipts"
              tooltip={`Receipts from ${recentRange.label}`}
              rows={recentReceipts}
              loading={refreshing}
              emptyText="No recent receipts found."
              onView={() =>
                navigate("/pawn/reports?type=receipt", {
                  state: {
                    ...userDataObject,
                    dateMode: "range",
                    fromDate: recentRange.fromDate,
                    toDate: recentRange.toDate,
                  },
                })
              }
              onRowClick={(r) =>
                navigate(r.settlement ? "/pawn/settlement" : "/pawn/receipt", {
                  state: {
                    ...userDataObject,
                    voucher_no: r.ticket,
                    receipt_ref: r.code,
                  },
                })
              }
            />
          </Box>
        </Grid>

        {/* Portfolio snapshot */}
        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              p: { xs: 1.75, md: 2.5 },
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: INK, mb: 2 }}
            >
              Portfolio Snapshot
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {[
                {
                  label: "Average active loan",
                  value: fmtFull(Math.round(stats.avgLoan)),
                },
                { label: "Active tickets", value: stats.activeCount },
                { label: "Settled tickets", value: stats.settledCount },
                { label: "Total tickets", value: visibleLoans.length },
              ].map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "0.82rem",
                  }}
                >
                  <Typography sx={{ color: INK_MUTED, fontSize: "0.82rem" }}>
                    {row.label}
                  </Typography>
                  {refreshing ? (
                    <Shimmer w={72} h={11} />
                  ) : (
                    <Typography
                      sx={{
                        color: INK,
                        fontWeight: 600,
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                      }}
                    >
                      {row.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Quick actions */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${BLUE_PALE} 0%, #E0E7FF 100%)`,
              border: "1px solid #C7D2FE",
              borderRadius: "14px",
              p: { xs: 1.75, md: 2.5 },
            }}
          >
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: INK, mb: 0.5 }}
            >
              Quick Actions
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED, mb: 2 }}>
              Jump straight to daily counter tasks
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {quickActions.map((a) => (
                <Box
                  key={a.label}
                  onClick={() => navigate(a.route, { state: userDataObject })}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "rgba(255,255,255,0.7)",
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      bgcolor: SURFACE,
                      transform: "translateX(2px)",
                      borderColor: "#C7D2FE",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      bgcolor: BLUE_PALE,
                      color: BLUE,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(a.icon, { sx: { fontSize: 18 } })}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: "0.82rem", fontWeight: 600, color: INK }}
                    >
                      {a.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
                      {a.desc}
                    </Typography>
                  </Box>
                  <ArrowForwardRoundedIcon
                    sx={{ fontSize: 16, color: INK_FAINT }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PawnShopDashboard;
